import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { ArrowLeft, User, Phone, MapPin, Briefcase, Calendar, Building2, Printer } from 'lucide-react';
import PhotoLightbox from '../components/PhotoLightbox';

const StaffDetail = () => {
  const { staffId } = useParams();
  const navigate = useNavigate();
  const [staff, setStaff] = useState(null);
  const [branch, setBranch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [photoOpen, setPhotoOpen] = useState(false);

  useEffect(() => {
    fetchStaffDetail();
  }, [staffId]);

  const fetchStaffDetail = async () => {
    try {
      const [staffRes, branchesRes] = await Promise.all([
        api.get(`/staff/${staffId}`),
        api.get('/branches')
      ]);
      setStaff(staffRes.data);
      // Find the branch for this staff member
      const staffBranch = branchesRes.data.find(b => b.id === staffRes.data.branch_id);
      setBranch(staffBranch);
    } catch (error) {
      toast.error('Failed to fetch staff details');
      navigate('/staff');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-700"></div>
      </div>
    );
  }

  if (!staff) {
    return (
      <div className="p-6 text-center">
        <p className="text-slate-500">Staff member not found</p>
        <Button onClick={() => navigate('/staff')} className="mt-4">
          Back to Staff
        </Button>
      </div>
    );
  }

  const getStaffTypeColor = (type) => {
    switch (type) {
      case 'BRC-MC':
        return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'CER-EP':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'MTS':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      default:
        return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  return (
    <div className="p-6 md:p-8 lg:p-10" data-testid="staff-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Button
          variant="outline"
          onClick={() => navigate('/staff')}
          className="print:hidden"
          data-testid="back-to-staff-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Staff
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Staff Details</h1>
        </div>
        <Button
          variant="outline"
          onClick={() => window.print()}
          className="print:hidden ml-auto"
          data-testid="print-staff-btn"
        >
          <Printer className="w-4 h-4 mr-2" />
          Print
        </Button>
      </div>

      {/* Staff Card */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden max-w-3xl">
        {/* Header with Photo */}
        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-6 border-b border-slate-200">
          <div className="flex items-center gap-6">
            {staff.photo_url ? (
              <img
                src={staff.photo_url.startsWith('data:') ? staff.photo_url : `${process.env.REACT_APP_BACKEND_URL}${staff.photo_url}`}
                alt={staff.name}
                className="w-24 h-28 rounded-lg object-cover border-2 border-white shadow-md cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => setPhotoOpen(true)}
                data-testid="staff-photo-thumbnail"
              />
            ) : (
              <div className="w-24 h-28 rounded-lg bg-emerald-100 border-2 border-white shadow-md flex items-center justify-center">
                <span className="text-3xl font-bold text-emerald-700">
                  {staff.name?.substring(0, 2).toUpperCase()}
                </span>
              </div>
            )}

            {staff.photo_url && (
              <PhotoLightbox
                src={staff.photo_url.startsWith('data:') ? staff.photo_url : `${process.env.REACT_APP_BACKEND_URL}${staff.photo_url}`}
                alt={staff.name}
                open={photoOpen}
                onClose={() => setPhotoOpen(false)}
              />
            )}
            <div>
              <h2 className="text-2xl font-bold text-slate-900">{staff.name}</h2>
              <span className={`inline-block mt-2 text-sm px-3 py-1 rounded-full font-medium border ${getStaffTypeColor(staff.staff_type)}`}>
                {staff.staff_type}
              </span>
              <span className={`inline-block ml-2 mt-2 text-sm px-3 py-1 rounded-full font-medium ${
                staff.status === 'active'
                  ? 'bg-green-100 text-green-800 border border-green-200'
                  : 'bg-red-100 text-red-800 border border-red-200'
              }`}>
                {staff.status}
              </span>
            </div>
          </div>
        </div>

        {/* Details */}
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-blue-50 text-blue-600">
                <Phone className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Mobile Number</p>
                <p className="font-medium text-slate-900">{staff.mobile}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-purple-50 text-purple-600">
                <User className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Gender</p>
                <p className="font-medium text-slate-900">{staff.gender}</p>
              </div>
            </div>

            <div className="flex items-start gap-3 md:col-span-2">
              <div className="p-2 rounded-lg bg-orange-50 text-orange-600">
                <MapPin className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Address</p>
                <p className="font-medium text-slate-900">{staff.address || 'Not specified'}</p>
                {(staff.district || staff.state) && (
                  <p className="text-sm text-slate-600 mt-1">
                    {[staff.district, staff.state].filter(Boolean).join(', ')}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-emerald-50 text-emerald-600">
                <Briefcase className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Staff Type</p>
                <p className="font-medium text-slate-900">{staff.staff_type}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-purple-50 text-purple-600">
                <Building2 className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Branch</p>
                <p className="font-medium text-slate-900">{branch?.name || 'Not assigned'}</p>
              </div>
            </div>

            {staff.date_of_joining && (
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-teal-50 text-teal-600">
                  <Calendar className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Date of Joining</p>
                  <p className="font-medium text-slate-900">{new Date(staff.date_of_joining).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
                </div>
              </div>
            )}

            {staff.date_of_birth && (
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-pink-50 text-pink-600">
                  <Calendar className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Date of Birth</p>
                  <p className="font-medium text-slate-900">{new Date(staff.date_of_birth).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
                </div>
              </div>
            )}

            {staff.social_caste && (
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-amber-50 text-amber-600">
                  <User className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Social Caste</p>
                  <p className="font-medium text-slate-900">{staff.social_caste}</p>
                </div>
              </div>
            )}

            {staff.job_type && (
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-indigo-50 text-indigo-600">
                  <Briefcase className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Job Type</p>
                  <p className="font-medium text-slate-900">{staff.job_type}</p>
                </div>
              </div>
            )}

            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-slate-50 text-slate-600">
                <Calendar className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Created At</p>
                <p className="font-medium text-slate-900">
                  {new Date(staff.created_at).toLocaleDateString('en-IN', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric'
                  })}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffDetail;
